import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

// ─── API ───
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Constants ───
export const PRODUCTS_PER_PAGE = 48;
export const SEARCH_DEBOUNCE_MS = 400;
export const MIN_SEARCH_LENGTH = 2;
export const PRODUCT_TITLE_MAX = 40;
export const CART_STORAGE_KEY = "ttw_cart";
export const WISHLIST_STORAGE_KEY = "ttw_wishlist";

// ─── Price Formatter ───
export const formatPrice = (price) => {
  if (!price) return "Op aanvraag";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);
};

// ─── Cart Hook ───
export const useCart = () => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items)); }, [items]);

  const addItem = useCallback((product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.slug === product.slug);
      if (existing) return prev.map((i) => i.slug === product.slug ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success("Toegevoegd aan winkelwagen");
  }, []);

  const removeItem = useCallback((slug) => setItems((prev) => prev.filter((i) => i.slug !== slug)), []);
  const updateQuantity = useCallback((slug, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, quantity: qty } : i)));
  }, []);
  const clearCart = useCallback(() => setItems([]), []);
  const total = items.reduce((sum, i) => sum + (i.price || 0) * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return { items, addItem, removeItem, updateQuantity, clearCart, total, count };
};

// ─── Wishlist Hook ─── (localStorage, browser-only)
export const useWishlist = () => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => { localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items)); }, [items]);

  const has = useCallback((slug) => items.some((i) => i.slug === slug), [items]);

  const toggle = useCallback((product) => {
    setItems((prev) => {
      if (prev.some((i) => i.slug === product.slug)) {
        toast("Verwijderd van verlanglijst");
        return prev.filter((i) => i.slug !== product.slug);
      }
      toast.success("Toegevoegd aan verlanglijst");
      return [...prev, {
        slug: product.slug,
        title: product.title,
        price: product.price,
        image_url: product.image_url,
        brand: product.brand,
        car_make: product.car_make,
        car_model: product.car_model,
        manufacturer_item: product.manufacturer_item,
      }];
    });
  }, []);

  const remove = useCallback((slug) => {
    setItems((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return { items, has, toggle, remove, clear, count: items.length };
};

// ─── Brand Metadata ───
export const BRAND_META = {
  "BC Racing": { tagline: "Coilovers & Suspension", accent: "#E8372C" },
  "GFB": { tagline: "Turbo & Blow-Off Valves", accent: "#FF6B00" },
  "HEL": { tagline: "Remslangen & Oil Coolers", accent: "#C0392B" },
  "K&N": { tagline: "Luchtfilters & Intake", accent: "#D4380D" },
  "Koyorad": { tagline: "Performance Radiatoren", accent: "#CF6A26" },
  "Performance Clutches": { tagline: "Koppelingssets & Vliegwielen", accent: "#A93226" },
  "Ultra Racing": { tagline: "Chassis Bracing & Strut Bars", accent: "#FF3B30" },
  "Mishimoto": { tagline: "Cooling & Performance", accent: "#1A8FE3" },
  "Airrex": { tagline: "Luchtvering Systemen", accent: "#3498DB" },
  "Remus": { tagline: "Performance Uitlaten", accent: "#C0C0C0" },
  "Probite": { tagline: "Remschijven & Remblokken", accent: "#FF6B35" },
  "Odin": { tagline: "Coilovers & Vering", accent: "#8E44AD" },
  "AutoR Tuning": { tagline: "Bodykit & Styling", accent: "#E74C3C" },
  "AEM Induction": { tagline: "Luchtinlaat Systemen", accent: "#2ECC71" },
  "AEM Electronics": { tagline: "Tuning Elektronica", accent: "#27AE60" },
  "SIX Performance": { tagline: "Performance Onderdelen", accent: "#F39C12" },
  "Injen": { tagline: "Cold Air Intake Systems", accent: "#3498DB" },
  "Invidia": { tagline: "Uitlaat Systemen", accent: "#95A5A6" },
  "Skunk2 Racing": { tagline: "Engine & Intake", accent: "#E74C3C" },
  "Liberty Walk": { tagline: "Widebody & Styling", accent: "#F1C40F" },
  "Megan Racing": { tagline: "Vering & Uitlaten", accent: "#E67E22" },
  "SIX Aerodynamics": { tagline: "Spoilers & Splitters", accent: "#F39C12" },
  "Auto Finesse": { tagline: "Car Care & Detailing", accent: "#2C3E50" },
  "Perfco Performance": { tagline: "Spacers & Adapters", accent: "#7F8C8D" },
  "Stanceparts": { tagline: "Air Cups & Suspension", accent: "#9B59B6" },
  "Seibon Carbon": { tagline: "Carbon Fiber Bodywork", accent: "#2C3E50" },
  "DBA": { tagline: "Remschijven & Remblokken", accent: "#CC0000" },
  "Xforce": { tagline: "Performance Uitlaten", accent: "#E91E63" },
  "Turbosmart": { tagline: "Turbo & Boost Control", accent: "#00B4D8" },
  "Whiteline": { tagline: "Suspension & Chassis", accent: "#76B900" },
  "ACL": { tagline: "Engine Bearings", accent: "#FF6600" },
  "ARP": { tagline: "Performance Bouten", accent: "#1565C0" },
  "Hawk": { tagline: "Performance Remblokken", accent: "#FFB300" },
  "Xtreme Clutch": { tagline: "Performance Koppelingen", accent: "#E040FB" },
  "Pedders": { tagline: "Suspension & Vering", accent: "#D32F2F" },
  "Davies Craig": { tagline: "Koeling & Waterpompen", accent: "#1976D2" },
  "Ryco": { tagline: "Filters", accent: "#388E3C" },
};

// ─── Brand Colors for image-less product placeholders ───
export const BRAND_COLORS = {
  "BC Racing": { bg: "#1a1a2e", text: "#e94560" },
  "GFB": { bg: "#1a2a1a", text: "#4caf50" },
  "HEL": { bg: "#2a1a1a", text: "#ff5722" },
  "K&N": { bg: "#1a1a2a", text: "#2196f3" },
  "Koyorad": { bg: "#2a2a1a", text: "#ff9800" },
  "Performance Clutches": { bg: "#1a2a2a", text: "#00bcd4" },
  "Ultra Racing": { bg: "#2a1a2a", text: "#e040fb" },
};

// ─── Brand Logos - All text-based ───
export const BRAND_LOGOS = {};


export const CSS_TEXT_LOGOS = {
  "BC Racing": { text: "BC RACING", color: "#F1C40F" },
  "GFB": { text: "GFB", color: "#F1C40F" },
  "HEL": { text: "HEL", color: "#F1C40F" },
  "K&N": { text: "K&N", color: "#F1C40F" },
  "Koyorad": { text: "KOYORAD", color: "#F1C40F" },
  "Performance Clutches": { text: "PERF CLUTCH", color: "#F1C40F" },
  "Ultra Racing": { text: "ULTRA RACING", color: "#F1C40F" },
  "Mishimoto": { text: "MISHIMOTO", color: "#F1C40F" },
  "Airrex": { text: "AIRREX", color: "#F1C40F" },
  "Remus": { text: "REMUS", color: "#F1C40F" },
  "Probite": { text: "PROBITE", color: "#F1C40F" },
  "Odin": { text: "ODIN", color: "#F1C40F" },
  "AutoR Tuning": { text: "AUTOR", color: "#F1C40F" },
  "AEM Induction": { text: "AEM", color: "#F1C40F" },
  "AEM Electronics": { text: "AEM", color: "#F1C40F" },
  "SIX Performance": { text: "SIX", color: "#F1C40F" },
  "Injen": { text: "INJEN", color: "#F1C40F" },
  "Invidia": { text: "INVIDIA", color: "#F1C40F" },
  "Skunk2 Racing": { text: "SKUNK2", color: "#F1C40F" },
  "Liberty Walk": { text: "LIBERTY WALK", color: "#F1C40F" },
  "Megan Racing": { text: "MEGAN", color: "#F1C40F" },
  "SIX Aerodynamics": { text: "SIX AERO", color: "#F1C40F" },
  "Auto Finesse": { text: "AUTO FINESSE", color: "#F1C40F" },
  "Perfco Performance": { text: "PERFCO", color: "#F1C40F" },
  "Stanceparts": { text: "STANCEPARTS", color: "#F1C40F" },
  "Seibon Carbon": { text: "SEIBON", color: "#F1C40F" },
  "DFI": { text: "DFI", color: "#F1C40F" },
  "Password:JDM": { text: "PASSWORD:JDM", color: "#F1C40F" },
  "Chargespeed": { text: "CHARGESPEED", color: "#F1C40F" },
  "AIT Racing": { text: "AIT RACING", color: "#F1C40F" },
  "SPL-Tuning": { text: "SPL", color: "#F1C40F" },
  "Hella": { text: "HELLA", color: "#F1C40F" },
  "GP-Race": { text: "GP-RACE", color: "#F1C40F" },
  "ATI/Autometer": { text: "ATI", color: "#F1C40F" },
  "Airlift Performance": { text: "AIRLIFT", color: "#F1C40F" },
  "Innovate Motorsports": { text: "INNOVATE", color: "#F1C40F" },
  "Perrin Performance": { text: "PERRIN", color: "#F1C40F" },
  "CarbonTrenz": { text: "CARBON", color: "#F1C40F" },
  "Takata Racing": { text: "TAKATA", color: "#F1C40F" },
  "RRS Race & Rally": { text: "RRS", color: "#F1C40F" },
  "SI Products": { text: "SI", color: "#F1C40F" },
  "Depo / TYC / Sonar Lighting": { text: "DEPO/TYC", color: "#F1C40F" },
  "DBA": { text: "DBA", color: "#F1C40F" },
  "Xforce": { text: "XFORCE", color: "#F1C40F" },
  "Turbosmart": { text: "TURBOSMART", color: "#F1C40F" },
  "Whiteline": { text: "WHITELINE", color: "#F1C40F" },
  "ACL": { text: "ACL", color: "#F1C40F" },
  "ARP": { text: "ARP", color: "#F1C40F" },
  "Hawk": { text: "HAWK", color: "#F1C40F" },
  "Xtreme Clutch": { text: "XTREME", color: "#F1C40F" },
  "Pedders": { text: "PEDDERS", color: "#F1C40F" },
  "Davies Craig": { text: "DAVIES CRAIG", color: "#F1C40F" },
  "Ryco": { text: "RYCO", color: "#F1C40F" },
  "SuperPro": { text: "SUPERPRO", color: "#F1C40F" },
};

export const BRAND_BG = {
};

// All brands use the same red background
export const DEFAULT_BRAND_BG = "linear-gradient(135deg, #1a0505, #0a0a0a)";

// ─── Page Number Generation ───
export function generatePageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}
